import React, { useState, useEffect } from "react";
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc } from "firebase/firestore";
import { db } from "../firebase";
import {
  TextField, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Typography, Box, IconButton
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddCircleIcon from "@mui/icons-material/AddCircle";

function SubeYonetimi() {
  const [subeler, setSubeler] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [newSube, setNewSube] = useState({ ad: "", konum: "" });
  const [editSube, setEditSube] = useState(null);

  useEffect(() => {
    const fetchSubeler = async () => {
      setLoading(true);
      try {
        const subelerCollection = collection(db, "subeler");
        const subelerSnapshot = await getDocs(subelerCollection);
        const subeList = subelerSnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
        setSubeler(subeList);
        setLoading(false);
      } catch (err) {
        console.error("Şubeleri alma hatası:", err);
        setError(`Hata: ${err.message}`);
        setLoading(false);
      }
    };
    fetchSubeler();
  }, []);

  const handleAddSube = async () => {
    try {
      const subelerCollection = collection(db, "subeler");
      await addDoc(subelerCollection, { ...newSube });
      setNewSube({ ad: "", konum: "" });
      setOpenAddDialog(false);
      const subelerSnapshot = await getDocs(subelerCollection);
      setSubeler(subelerSnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
      setError("");
    } catch (err) {
      console.error("Şube ekleme hatası:", err);
      setError(`Hata: ${err.message}`);
    }
  };

  const handleEditSube = async () => {
    try {
      const subeDoc = doc(db, "subeler", editSube.id);
      await updateDoc(subeDoc, { ...editSube });
      setOpenEditDialog(false);
      const subelerCollection = collection(db, "subeler");
      const subelerSnapshot = await getDocs(subelerCollection);
      setSubeler(subelerSnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
      setError("");
    } catch (err) {
      console.error("Şube güncelleme hatası:", err);
      setError(`Hata: ${err.message}`);
    }
  };

  const handleDeleteSube = async (id) => {
    if (window.confirm("Bu şubeyi silmek istediğinize emin misiniz?")) {
      try {
        const subeDoc = doc(db, "subeler", id);
        await deleteDoc(subeDoc);
        setSubeler(subeler.filter((s) => s.id !== id));
        setError("");
      } catch (err) {
        console.error("Şube silme hatası:", err);
        setError(`Hata: ${err.message}`);
      }
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <Typography variant="h6" color="textSecondary">Yükleniyor...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <Typography variant="h6" color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box
      className="sy-page"
      sx={{
        p: 2,
        background: "linear-gradient(165deg, #f5f2eb 0%, #ece6da 45%, #e2dacd 100%)",
        minHeight: "auto",
        fontFamily: "'Inter', 'Segoe UI', sans-serif"
      }}
    >
      <Paper
        className="sy-paper"
        elevation={0}
        sx={{
          borderRadius: 3,
          overflow: "hidden",
          border: "1px solid rgba(212, 175, 98, 0.28)",
          boxShadow: "0 14px 30px rgba(15, 23, 42, 0.16), inset 0 1px 0 rgba(255,255,255,0.7)"
        }}
      >
        <Box className="sy-header" sx={{ background: "linear-gradient(130deg, #121820 0%, #0c1118 52%, #070b10 100%)", color: "#f4f1eb", p: 2.5, textAlign: "center", borderBottom: "1px solid rgba(212, 175, 98, 0.22)" }}>
          <Typography className="sy-page-title" variant="h5" fontWeight={600} sx={{ letterSpacing: "0.02em", fontFamily: "Georgia, serif", fontSize: "2rem" }}>Şube Yönetimi</Typography>
        </Box>

        <Box className="sy-content" sx={{ p: 2.5, background: "linear-gradient(180deg, rgba(252,251,248,0.98), rgba(245,241,235,0.96))" }}>
          <Box className="sy-toolbar" sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
            <Button
              className="sy-add-btn"
              variant="contained"
              startIcon={<AddCircleIcon />}
              onClick={() => setOpenAddDialog(true)}
              sx={{
                background: "linear-gradient(180deg, #1f2937 0%, #0f172a 100%)",
                border: "1px solid rgba(212, 175, 98, 0.4)",
                fontWeight: 700,
                textTransform: "none",
                px: 2.5,
                py: 0.9,
                fontSize: "0.88rem",
                borderRadius: "10px",
                boxShadow: "0 8px 18px rgba(15, 23, 42, 0.28)",
                "&:hover": { background: "linear-gradient(180deg, #273549 0%, #152235 100%)" }
              }}
            >
              Yeni Şube Ekle
            </Button>
          </Box>

          <TableContainer
            className="sy-table-wrap"
            component={Paper}
            elevation={0}
            sx={{
              borderRadius: 2.5,
              border: "1px solid rgba(212, 175, 98, 0.22)",
              boxShadow: "0 8px 20px rgba(15,23,42,0.07)"
            }}
          >
            <Table className="sy-table" size="small">
              <TableHead>
                <TableRow sx={{ background: "linear-gradient(180deg, #111827 0%, #0b1220 100%)" }}>
                  <TableCell sx={{ color: "#f8fafc", fontWeight: 800, fontSize: "0.86rem", letterSpacing: "0.03em", py: 1.1 }}>Şube Adı</TableCell>
                  <TableCell sx={{ color: "#f8fafc", fontWeight: 800, fontSize: "0.86rem", letterSpacing: "0.03em", py: 1.1 }}>Konum</TableCell>
                  <TableCell sx={{ color: "#f8fafc", fontWeight: 800, fontSize: "0.86rem", letterSpacing: "0.03em", py: 1.1 }} align="center">İşlemler</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {subeler.map((sube) => (
                  <TableRow
                    key={sube.id}
                    hover
                    sx={{
                      "&:nth-of-type(odd)": { backgroundColor: "rgba(248, 250, 252, 0.9)" },
                      "&:nth-of-type(even)": { backgroundColor: "rgba(241, 245, 249, 0.75)" },
                      "&:hover": { backgroundColor: "rgba(212,175,98,0.12)" }
                    }}
                  >
                    <TableCell className="sy-cell-name" data-label="Şube Adı" sx={{ fontWeight: 700, color: "#111827", fontSize: "0.86rem", py: 0.85 }}>{sube.ad}</TableCell>
                    <TableCell className="sy-cell-location" data-label="Konum" sx={{ color: "#334155", fontWeight: 500, fontSize: "0.82rem", py: 0.85 }}>{sube.konum}</TableCell>
                    <TableCell className="sy-cell-actions" data-label="İşlemler" align="center">
                      <IconButton
                        size="small"
                        onClick={() => {
                          setEditSube(sube);
                          setOpenEditDialog(true);
                        }}
                        sx={{
                          color: "#1e293b",
                          mr: 1,
                          border: "1px solid rgba(148,163,184,0.35)",
                          background: "rgba(255,255,255,0.7)",
                          "&:hover": { background: "rgba(212,175,98,0.18)" }
                        }}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteSube(sube.id)}
                        sx={{
                          color: "#b91c1c",
                          border: "1px solid rgba(239,68,68,0.3)",
                          background: "rgba(255,255,255,0.7)",
                          "&:hover": { background: "rgba(254,226,226,0.8)" }
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Paper>

      {/* Yeni Şube Ekle Dialog */}
      <Dialog className="sy-dialog" open={openAddDialog} onClose={() => setOpenAddDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ background: "linear-gradient(180deg, #111827 0%, #0b1220 100%)", color: "#f8fafc", fontWeight: 700 }}>
          Yeni Şube Ekle
        </DialogTitle>
        <DialogContent sx={{ pt: 3, background: "linear-gradient(180deg, #fcfbf8 0%, #f5f1eb 100%)" }}>
          <TextField label="Şube Adı" fullWidth margin="normal" value={newSube.ad} onChange={(e) => setNewSube({ ...newSube, ad: e.target.value })} />
          <TextField label="Konum" fullWidth margin="normal" value={newSube.konum} onChange={(e) => setNewSube({ ...newSube, konum: e.target.value })} />
        </DialogContent>
        <DialogActions sx={{ p: 3, background: "linear-gradient(180deg, #fcfbf8 0%, #f5f1eb 100%)" }}>
          <Button onClick={() => setOpenAddDialog(false)} sx={{ color: "#b91c1c", fontWeight: 700 }}>İptal</Button>
          <Button onClick={handleAddSube} variant="contained" sx={{ background: "linear-gradient(180deg, #1f2937 0%, #0f172a 100%)", border: "1px solid rgba(212,175,98,0.35)", fontWeight: 700 }}>Ekle</Button>
        </DialogActions>
      </Dialog>

      {/* Şube Düzenle Dialog */}
      {editSube && (
        <Dialog className="sy-dialog" open={openEditDialog} onClose={() => setOpenEditDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ background: "linear-gradient(180deg, #111827 0%, #0b1220 100%)", color: "#f8fafc", fontWeight: 700 }}>
            Şube Düzenle
          </DialogTitle>
          <DialogContent sx={{ pt: 3, background: "linear-gradient(180deg, #fcfbf8 0%, #f5f1eb 100%)" }}>
            <TextField label="Şube Adı" fullWidth margin="normal" value={editSube.ad} onChange={(e) => setEditSube({ ...editSube, ad: e.target.value })} />
            <TextField label="Konum" fullWidth margin="normal" value={editSube.konum} onChange={(e) => setEditSube({ ...editSube, konum: e.target.value })} />
          </DialogContent>
          <DialogActions sx={{ p: 3, background: "linear-gradient(180deg, #fcfbf8 0%, #f5f1eb 100%)" }}>
            <Button onClick={() => setOpenEditDialog(false)} sx={{ color: "#b91c1c", fontWeight: 700 }}>İptal</Button>
            <Button onClick={handleEditSube} variant="contained" sx={{ background: "linear-gradient(180deg, #1f2937 0%, #0f172a 100%)", border: "1px solid rgba(212,175,98,0.35)", fontWeight: 700 }}>Kaydet</Button>
          </DialogActions>
        </Dialog>
      )}
      <style>{`
        /* --- MOBİL: Şube Yönetimi (masaüstü sx stillerine dokunulmaz) --- */
        @media (max-width: 1649px), (max-width: 1400px) and (any-pointer: coarse) {
          .sy-page {
            width: 100% !important;
            max-width: 100% !important;
            min-width: 0 !important;
            min-height: 0 !important;
            height: auto !important;
            overflow-x: hidden !important;
            padding: 6px 2px 80px !important;
            box-sizing: border-box !important;
          }

          .sy-paper {
            width: 100% !important;
            max-width: 100% !important;
            min-width: 0 !important;
            border-radius: 12px !important;
          }

          .sy-header {
            padding: 16px 12px !important;
          }

          .sy-page-title {
            font-size: 1.35rem !important;
            line-height: 1.2 !important;
          }

          .sy-content {
            padding: 12px 8px !important;
          }

          .sy-toolbar {
            justify-content: stretch !important;
            margin-bottom: 12px !important;
          }

          .sy-add-btn {
            width: 100% !important;
            min-height: 44px !important;
          }

          .sy-table-wrap {
            overflow: visible !important;
            border: none !important;
            box-shadow: none !important;
            background: transparent !important;
          }

          .sy-table {
            min-width: 0 !important;
          }

          .sy-table thead {
            display: none !important;
          }

          .sy-table tbody tr {
            display: block !important;
            margin-bottom: 10px !important;
            border: 1px solid rgba(212, 175, 98, 0.22) !important;
            border-radius: 12px !important;
            overflow: hidden !important;
            background: rgba(255, 255, 255, 0.95) !important;
            box-shadow: 0 4px 12px rgba(15, 23, 42, 0.06) !important;
          }

          .sy-table tbody tr:nth-of-type(odd),
          .sy-table tbody tr:nth-of-type(even) {
            background-color: rgba(255, 255, 255, 0.95) !important;
          }

          .sy-table tbody td {
            display: flex !important;
            justify-content: space-between !important;
            align-items: center !important;
            gap: 10px !important;
            padding: 10px 12px !important;
            border: none !important;
            border-bottom: 1px solid rgba(15, 23, 42, 0.06) !important;
            text-align: right !important;
            font-size: 13px !important;
            word-break: break-word !important;
          }

          .sy-table tbody td:last-child {
            border-bottom: none !important;
            justify-content: flex-end !important;
          }

          .sy-table tbody td::before {
            content: attr(data-label) !important;
            font-weight: 700 !important;
            color: #64748b !important;
            font-size: 12px !important;
            text-align: left !important;
            flex-shrink: 0 !important;
          }

          .sy-cell-actions::before {
            margin-right: auto !important;
          }

          .sy-dialog .MuiDialog-paper {
            width: calc(100vw - 32px) !important;
            max-width: calc(100vw - 32px) !important;
            margin: 16px !important;
          }

          .sy-dialog .MuiInputBase-input {
            font-size: 16px !important;
          }

          .sy-dialog .MuiDialogActions-root {
            flex-wrap: wrap !important;
            gap: 8px !important;
          }

          .sy-dialog .MuiDialogActions-root .MuiButton-root {
            flex: 1 1 auto !important;
            min-height: 40px !important;
          }
        }
      `}</style>
    </Box>
  );
}

export default SubeYonetimi;